import React from 'react';
import { BookOpen, CheckCircle, Circle, SkipForward, Split, Star } from 'lucide-react';
import { QUESTION_RESPONSE_STATUS, QUESTION_TYPES } from '../../../constants';
import { useParams } from '../../../hooks';
import { getAssignmentQuestionURL, getReviewQuestionUrl } from '../../../utils/urlHelpers';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../ui/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  alternativeRows,
  batchDrawerAlternatives,
  buildDrawerQuestionNumbers,
  buildDrawerSectionRows,
  buildDrawerSubQuestionRows,
  collectDrawerChoiceGroups,
} from '../../../utils/drawerRows';

const Section = (props) => {
  const { questions, closeDrawer, isReview, assignmentQuestionSections, assignmentQuestions } =
    props;
  const { studentAssignmentId, questionId } = useParams();
  const navigate = useNavigate();

  const rcSubQuestionIds = React.useMemo(() => {
    const ids = new Set();
    assignmentQuestions?.forEach((question) => {
      if (question.type === QUESTION_TYPES.READING_COMPREHENSION) {
        question.subQuestions?.forEach((subQuestionId) => ids.add(subQuestionId));
      }
    });
    return ids;
  }, [assignmentQuestions]);


  const groupedQuestions = React.useMemo(() => {
    if (!questions) return [];

    const questionIndexMap = questions.reduce((acc, question, index) => {
      acc[question.questionId] = index + 1;
      return acc;
    }, {});

    const isInSection = (section, responseQuestionId) => {
      if (section.questions.includes(responseQuestionId)) {
        return true;
      }

      return assignmentQuestions?.some(
        (question) =>
          question.type === QUESTION_TYPES.READING_COMPREHENSION &&
          section.questions.includes(question.id) &&
          question.subQuestions?.includes(responseQuestionId)
      );
    };

    const mapSectionQuestions = (section) =>
      questions
        .filter((question) => isInSection(section, question.questionId))
        .map((question) => ({
          ...question,
          globalIndex: questionIndexMap[question.questionId]
        }));

    if (!assignmentQuestionSections?.length) {
      return [
        {
          id: 'default',
          name: 'Section 1',
          questions: questions.map((question, index) => ({
            ...question,
            globalIndex: index + 1
          }))
        }
      ];
    }

    return assignmentQuestionSections
      .map((section) => ({
        ...section,
        questions: mapSectionQuestions(section)
      }))
      .filter((section) => section.questions.length > 0);
  }, [assignmentQuestionSections, assignmentQuestions, questions]);

  const handleQuestionClick = (clickedQuestionId) => {
    if (questionId !== clickedQuestionId) {
      const redirectUrlFn = isReview ? getReviewQuestionUrl : getAssignmentQuestionURL;
      navigate(redirectUrlFn({ studentAssignmentId, questionId: clickedQuestionId }));
      closeDrawer();
    }
  };

  const getQuestionCellStyle = (status, isBookmarked, isCurrent) => {
    if (isCurrent) return 'border-primary bg-primary/5 ring-2 ring-primary/20';
    if (status === QUESTION_RESPONSE_STATUS.ANSWERED) return 'border-green-200 bg-green-50';
    if (status === QUESTION_RESPONSE_STATUS.SKIPPED) return 'border-orange-200 bg-orange-50';
    if (isBookmarked) return 'border-yellow-200 bg-yellow-50';
    return 'border-border hover:border-primary/30';
  };

  const getQuestionIcon = (status, isBookmarked, isCurrent) => {
    // Bookmark wins the icon slot; the cell background still conveys answered/skipped.
    if (isBookmarked) {
      return <Star className="h-3.5 w-3.5 fill-current text-yellow-500" />;
    }
    if (status === QUESTION_RESPONSE_STATUS.ANSWERED) {
      return <CheckCircle className="h-3.5 w-3.5 text-green-600" />;
    }
    if (status === QUESTION_RESPONSE_STATUS.SKIPPED) {
      return <SkipForward className="h-3.5 w-3.5 text-orange-500" />;
    }
    if (isCurrent) {
      return <Circle className="h-3.5 w-3.5 fill-current text-primary" />;
    }
    return <Circle className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  const numberFor = (id, fallback) => displayNumbers[String(id)] ?? String(fallback ?? '');

  /** Every choice group on the paper, so a passage block can find the ones inside it. */
  const allChoiceGroups = React.useMemo(
    () => collectDrawerChoiceGroups(assignmentQuestionSections),
    [assignmentQuestionSections]
  );

  const displayNumbers = React.useMemo(
    () =>
      buildDrawerQuestionNumbers({
        sections: assignmentQuestionSections,
        assignmentQuestions,
        responses: questions,
      }),
    [assignmentQuestionSections, assignmentQuestions, questions]
  );

  const buildSectionRows = (section) =>
    buildDrawerSectionRows({ section, rows: section.questions, assignmentQuestions });

  const buildSubQuestionRows = (parent, rcSubs) =>
    buildDrawerSubQuestionRows({ parent, rows: rcSubs, allChoiceGroups });

  /** The groups its sub-questions choose among. A passage may hold more than one. */
  const subQuestionGroupsFor = (passageId) =>
    allChoiceGroups.filter((group) => String(group.parentQuestionId) === String(passageId));

  /** One question's cell, shared by the loose grid and a choice group's own grid. */
  const renderQuestionCell = ({ questionId: id, status, isBookmarked, globalIndex }) => {
    const isCurrent = id === questionId;
    return (
      <Button
        key={id}
        type="button"
        variant="outline"
        size="sm"
        onClick={() => handleQuestionClick(id)}
        className={cn(
          'flex h-14 flex-col items-center justify-center gap-1 p-2',
          getQuestionCellStyle(status, isBookmarked, isCurrent)
        )}
      >
        {getQuestionIcon(status, isBookmarked, isCurrent)}
        <span className="text-xs font-medium">Q{numberFor(id, globalIndex)}</span>
      </Button>
    );
  };

  /**
   * An "attempt any X of these Y" block. An alternative is a question or a whole passage, and a
   * passage counts as attempted once any of its questions is answered.
   */
  const renderChoiceGroup = ({ group, alternatives }) => {
    // A passage alternative highlights its own block, so the rule's outline follows only the
    // questions offered directly.
    const isGroupActive = alternatives.some(
      (alternative) => alternative.type === 'question' && alternative.item.questionId === questionId
    );
    const answeredCount = alternatives.filter((alternative) =>
      alternativeRows(alternative).some(
        (member) => member.status === QUESTION_RESPONSE_STATUS.ANSWERED
      )
    ).length;
    // Answering more than required is allowed, so the bar is capped rather than
    // overflowing - it tracks progress towards the requirement, not effort spent.
    const requiredCount = group.requiredCount || 0;
    const progressPct =
      requiredCount > 0
        ? Math.min(100, (answeredCount / requiredCount) * 100)
        : 100;

    return (
      <div
        key={group.id}
        className={cn(
          'rounded-lg border p-3',
          isGroupActive
            ? 'border-primary/40 bg-primary/5'
            : 'border-purple-200 bg-purple-50/40'
        )}
      >
        <div className="mb-2.5 flex items-start gap-2">
          <Split className="mt-0.5 h-4 w-4 flex-shrink-0 text-purple-600" />
          <div className="min-w-0 flex-1">
            <span className="text-xs font-semibold text-purple-800">
              Attempt any {requiredCount} of {alternatives.length}
            </span>
          </div>
        </div>

        <div className="mb-2.5 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-green-500 transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {answeredCount}/{requiredCount} done
          </span>
        </div>

        <div className="space-y-2">
          {batchDrawerAlternatives(alternatives).map((part, partIndex) =>
            part.type === 'rc' ? (
              renderRcBlock(part)
            ) : (
              <div key={`alternatives-${partIndex}`} className="grid grid-cols-3 gap-1.5">
                {part.items.map(renderQuestionCell)}
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  /** A reading-comprehension passage and its questions. */
  const renderRcBlock = ({ parent, subQuestions: rcSubs }) => {
    const isGroupActive = rcSubs.some((sq) => sq.questionId === questionId);
    const answeredCount = rcSubs.filter(
      (sq) => sq.status === QUESTION_RESPONSE_STATUS.ANSWERED
    ).length;
    const totalSubQ = rcSubs.length;
    const isBookmarked = rcSubs.some((sq) => sq.isBookmarked);

    return (
      <div
        key={parent.id}
        className={cn(
          'rounded-lg border p-3',
          isGroupActive
            ? 'border-primary/40 bg-primary/5'
            : 'border-indigo-200 bg-indigo-50/40'
        )}
      >
        <div className="mb-2.5 flex items-start gap-2">
          <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-600" />
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-indigo-800">
                Q{numberFor(parent.id, rcSubs[0]?.globalIndex)}
              </span>
              <Badge className="h-4 bg-indigo-100 px-1.5 py-0 text-xs text-indigo-700">
                Reading Comprehension
              </Badge>
              {/* A choice among the passage's own questions changes what has to be answered.
                  A rule offering the whole passage draws its own box around this block. */}
              {(() => {
                const insideGroups = subQuestionGroupsFor(parent.id);
                const [insideGroup] = insideGroups;
                const label =
                  insideGroups.length > 1
                    ? `${insideGroups.length} choices below`
                    : insideGroup
                      ? `Any ${insideGroup.requiredCount} of ${(insideGroup.questions || []).length} below`
                      : null;
                if (!label) return null;
                return (
                  <Badge className="h-4 bg-purple-100 px-1.5 py-0 text-xs text-purple-700">
                    {label}
                  </Badge>
                );
              })()}
            </div>
          </div>
          {isBookmarked && (
            <Star className="h-3.5 w-3.5 flex-shrink-0 fill-current text-yellow-500" />
          )}
        </div>

        <div className="mb-2.5 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-green-500 transition-all"
              style={{
                width: totalSubQ > 0 ? `${(answeredCount / totalSubQ) * 100}%` : '0%'
              }}
            />
          </div>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {answeredCount}/{totalSubQ} done
          </span>
        </div>

        {(() => {
          const renderSub = ({ questionId: id, status, globalIndex }) => {
            const isCurrentSub = id === questionId;
            const subIcon =
              status === QUESTION_RESPONSE_STATUS.ANSWERED ? (
                <CheckCircle className="h-3 w-3 text-green-600" />
              ) : status === QUESTION_RESPONSE_STATUS.SKIPPED ? (
                <SkipForward className="h-3 w-3 text-orange-500" />
              ) : isCurrentSub ? (
                <Circle className="h-3 w-3 fill-current text-primary" />
              ) : (
                <Circle className="h-3 w-3 text-muted-foreground" />
              );

            return (
              <Button
                key={id}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuestionClick(id)}
                className={cn(
                  'flex h-11 flex-col items-center justify-center gap-0.5 p-1',
                  getQuestionCellStyle(status, false, isCurrentSub)
                )}
              >
                {subIcon}
                <span className="text-xs font-medium leading-none">
                  {numberFor(id, globalIndex)}
                </span>
              </Button>
            );
          };

          // A choice among the passage's own questions is drawn inside its own outline, the
          // same way a section's groups are, and in the position its first question holds -
          // otherwise every loose question jumps above every group.
          return (
            <div className="space-y-2">
              {buildSubQuestionRows(parent, rcSubs).map((row, rowIndex) => {
                if (row.type === 'questions') {
                  return (
                    <div key={`subs-${rowIndex}`} className="grid grid-cols-4 gap-1.5">
                      {row.items.map(renderSub)}
                    </div>
                  );
                }

                const answeredInGroup = row.members.filter(
                  (sub) => sub.status === QUESTION_RESPONSE_STATUS.ANSWERED
                ).length;

                return (
                  <div
                    key={row.group.id}
                    className="rounded-lg border-2 border-purple-300 bg-purple-50/40 p-2"
                  >
                    <div className="mb-1.5 flex items-center gap-1.5">
                      <Split className="h-3 w-3 flex-shrink-0 text-purple-600" />
                      <span className="text-[11px] font-semibold text-purple-800">
                        Attempt any {row.group.requiredCount} of {row.members.length}
                      </span>
                      <span className="ml-auto whitespace-nowrap text-[11px] text-muted-foreground">
                        {answeredInGroup}/{row.group.requiredCount} done
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">{row.members.map(renderSub)}</div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="mt-2 space-y-5">
      {groupedQuestions.map((section) => {
        const sectionRows = buildSectionRows(section);

        return (
          <div key={section.id}>
            <h3 className="mb-2.5 flex items-center gap-2 text-sm font-medium text-foreground">
              {section.name}
              <span className="text-xs font-normal text-muted-foreground">
                ({section.questions.length}{' '}
                {section.questions.length === 1 ? 'question' : 'questions'})
              </span>
            </h3>

            <div className="space-y-2.5">
              {/* One pass in paper order: a block appears where its first question sits,
                  so a group of Q2-Q4 falls between Q1 and Q5 rather than after them. */}
              {sectionRows.map((row, rowIndex) => {
                if (row.type === 'choiceGroup') return renderChoiceGroup(row);
                if (row.type === 'rc') return renderRcBlock(row);
                return (
                  <div key={`questions-${rowIndex}`} className="grid grid-cols-3 gap-2">
                    {row.items.map(renderQuestionCell)}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export { Section };
